// Key Highlights for the daily brief.
//
// The brief and the board pack must not disagree about what matters, so this
// runs the SAME registry, evidence builder and selector as the pack rather than
// a parallel implementation. It only adapts the inputs: the brief holds a
// `CfoInsightsResponse` plus the metric comparisons, not a full presentation
// response, so it feeds a minimal shape carrying exactly the fields the shared
// builder reads.
//
// The consequence is honest and worth stating: without statements or an A/R
// analysis payload, statement-variance and receivables templates cannot resolve
// here, and the brief draws only the trend, progress and dual-axis families.
// That is the correct outcome — it renders fewer highlights rather than
// inventing evidence it does not have — and it lifts automatically once the
// brief is given a richer payload.

import type { CfoInsightsResponse, MetricComparisonResponse, QuickBooksCfoPresentationResponse } from '../../../../data/ServerApiClient'
import { buildHighlightEvidence, reportingPeriodState } from './evidence'
import { selectKeyHighlights, withCurrentPriorityCandidates } from './selection'
import type { HighlightSelectionResult, HighlightVisualEvidence, KeyHighlight } from './types'

/** An empty statement render — present in shape, empty in content. */
const emptyStatement = (statementType: string) => ({
  connection_id: '',
  layout_version_id: '',
  statement_type: statementType,
  balance_sheet_mode: '',
  periods: [] as string[],
  rows: [] as never[],
  warnings: [] as string[],
})

export type BriefHighlightsResult = HighlightSelectionResult & {
  evidence: HighlightVisualEvidence[]
  /** True when the reporting period has not closed. */
  periodOpen: boolean
}

/**
 * Select up to `limit` highlights for the brief.
 *
 * `hiddenDetectors` replaces the brief's bespoke `HOME_HIDDEN_INSIGHT_DETECTORS`
 * filter, so suppression is expressed once, in the selector, instead of being
 * applied in one place and re-derived in another.
 */
export function selectBriefHighlights(args: {
  insights: CfoInsightsResponse | null
  comparisons?: MetricComparisonResponse | null
  /**
   * The full presentation payload, when it has arrived.
   *
   * With it, the brief resolves the same families the board pack does —
   * statement variance, the P&L bridge, working capital, A/R. Without it the
   * adapter falls back to the minimal shape below and only the trend, progress
   * and dual-axis families can resolve. The brief therefore renders immediately
   * with whatever it has and upgrades in place when this lands, rather than
   * blocking first paint on a second request.
   */
  presentation?: QuickBooksCfoPresentationResponse | null
  dataThrough?: string | null
  previous?: KeyHighlight[]
  hiddenDetectors?: readonly string[]
  limit?: number
  /**
   * Fill a quiet detector set from governed metric comparisons.
   *
   * Used by the cockpit, where an expanded company must always have a useful
   * current read. The generated candidates contain no invented values or prose:
   * they are deterministic projections of the served comparison contract.
   */
  ensureCurrentPriorities?: boolean
}): BriefHighlightsResult {
  const empty: BriefHighlightsResult = {
    highlights: [],
    rejections: [],
    evidence: [],
    periodOpen: false,
    visualSelectionVersion: '',
    visualEvidenceSchemaVersion: '',
  }
  // Prefer the real payload; the stub is the pre-arrival fallback.
  const adapted = args.presentation ?? {
    statements: {
      profit_and_loss: emptyStatement('profit_and_loss'),
      balance_sheet: emptyStatement('balance_sheet'),
      cash_flow: emptyStatement('cash_flow'),
    },
    metric_comparisons: args.comparisons || null,
    ar_analysis: null,
    source_state: {
      data_through: args.dataThrough || args.insights?.as_of || null,
      reconciliation_status: null,
    },
  } as unknown as QuickBooksCfoPresentationResponse

  // The clock read belongs outside the purity-guarded evidence module.
  const now = new Date()
  const { periodOpen } = reportingPeriodState(adapted, { year: now.getUTCFullYear(), month: now.getUTCMonth() + 1 })
  const initialCards = args.insights?.cards || []
  const cards = args.ensureCurrentPriorities
    ? withCurrentPriorityCandidates(
      initialCards,
      adapted.metric_comparisons || args.comparisons || null,
      Math.max(3, args.limit ?? 3),
      { periodOpen },
    )
    : initialCards
  if (!cards.length) return { ...empty, periodOpen }
  const evidence = buildHighlightEvidence(adapted, cards)
  const selection = selectKeyHighlights({
    insights: cards,
    evidence,
    previous: args.previous,
    periodOpen,
    limit: args.limit ?? 3,
    hiddenDetectors: args.hiddenDetectors,
  })

  return { ...selection, evidence, periodOpen }
}
